import { ArrowLineDown, CloudArrowUp, File } from '@phosphor-icons/react';
import { useState } from 'react';
import { Button, Flex, Modal, Spin, Typography, Upload } from 'antd';
import { COLOR } from '@styles/theme';
import { CommonUtility, LocationFeedService, ToastMessage } from '@utility/index';
import { UploadService } from '@utility/services/upload';
import { LocationFeedList } from '../location-feed';

const { Text } = Typography;

function TemplateDownloadButton({ label, loading, onClick }) {
  return (
    <Button
      block
      disabled={loading}
      onClick={onClick}
      style={{ height: 'auto', padding: '8px 12px' }}
    >
      <Flex align="center" justify="space-between" style={{ width: '100%' }}>
        <Flex align="center" gap={6}>
          <File size={15} color={COLOR.textSecondary} />
          <span>{label}</span>
        </Flex>
        <Flex align="center" gap={6}>
          <ArrowLineDown size={15} />
          <span>Download</span>
        </Flex>
      </Flex>
    </Button>
  );
}

export function LocationFeedPopup({ open, onClose, warehouseId }) {
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const feedType = 'location-upload';

  const downloadTemplate = async exportFeed => {
    try {
      setDownloading(true);
      const params = { export: exportFeed, warehouseId };
      const res = await LocationFeedService.getFeedTemplate(feedType, params);
      CommonUtility.downloadCSV(
        res,
        `Location Feed ${exportFeed ? 'Pre-Filled' : 'Blank'} Template`,
      );
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setDownloading(false);
    }
  };

  const onConfirm = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      ToastMessage.error('Please select a CSV file to upload.');
      return;
    }
    try {
      setLoading(true);
      const res = await LocationFeedService.generateUrl(feedType, { warehouseId });

      if (res.uploadUrl) {
        await UploadService.media(res.uploadUrl, file);
        ToastMessage.success(
          'File uploaded successfully, check the feed tab for progress',
        );
        setFileList([]);
        setShowLogs(true);
      } else {
        ToastMessage.error('There was some error uploading file.');
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const hasFile = Boolean(fileList[0]?.originFileObj);

  return (
    <Modal
      title="Import Location Feed"
      open={open}
      centered
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={520}
    >
      <Flex vertical gap={16}>
        <div>
          <Text type="secondary">Templates</Text>
          <Spin
            spinning={downloading}
            tip="Downloading…"
            style={{ display: 'block', marginTop: 8 }}
          >
            <Flex gap={8} style={{ marginTop: 8 }}>
              <TemplateDownloadButton
                label="Pre-Filled"
                loading={downloading}
                onClick={() => downloadTemplate(true)}
              />
              <TemplateDownloadButton
                label="Blank"
                loading={downloading}
                onClick={() => downloadTemplate(false)}
              />
            </Flex>
          </Spin>
        </div>

        <div>
          <Text type="secondary">Upload</Text>
          <Upload
            accept=".csv"
            beforeUpload={() => false}
            maxCount={1}
            fileList={fileList}
            onChange={({ fileList: next }) => setFileList(next)}
            disabled={loading}
            style={{ marginTop: 8, display: 'block' }}
          >
            <Button disabled={loading}>Choose CSV file</Button>
          </Upload>
        </div>

        {showLogs ? <LocationFeedList warehouseId={warehouseId} /> : null}

        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Button
            type="link"
            onClick={() => setShowLogs(!showLogs)}
            style={{ padding: 0 }}
          >
            {showLogs ? 'Hide Past Imports' : 'See Past Imports'}
          </Button>
          <Flex gap={8}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={onConfirm}
              disabled={!hasFile}
              icon={<CloudArrowUp size={17} />}
            >
              Upload
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
}
